/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Button, Icon, Image, Sidebar } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { COLORS } from '@/constants';
import { getHubeeDocumentContenu } from '@/routes';
import { extractAllPdfPages } from '@/utils/pdf-helpers';

/** Type HubEE du récapitulatif de la demande saisie par les partenaires */
const TYPE_RECAPITULATIF = 'recapPDF';

/** Largeur de rendu des pages (px), pour une lecture confortable */
const LARGEUR_PAGE = 800;

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binaire = '';
  // Par blocs : String.fromCharCode(...bytes) dépasse la pile sur un gros PDF
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binaire += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binaire);
};

interface SidebarRecapitulatifProps {
  identifiantTeledossier: string;
  /** Télédossier chargé (détail de la demande, avec ses pièces jointes) */
  teledossier: any;
}

/**
 * Volet latéral de l'assistant en mode Hubee : pages du récapitulatif de la
 * demande, pour la saisie en vis-à-vis. Même principe que la notice
 * individuelle de l'assistant RCO.
 */
const SidebarRecapitulatif = (props: SidebarRecapitulatifProps) => {
  const { identifiantTeledossier, teledossier } = props;

  const [visible, setVisible] = React.useState(false);
  const [pages, setPages] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [erreur, setErreur] = React.useState(false);

  const recapitulatif = React.useMemo(
    () =>
      _.find(
        teledossier?.piecesJointes,
        (pj: any) => pj?.typeHubee === TYPE_RECAPITULATIF,
      ),
    [teledossier],
  );

  React.useEffect(() => {
    if (!recapitulatif) {
      return undefined;
    }

    let ignore = false;

    const afficher = (base64: string) =>
      extractAllPdfPages(base64, LARGEUR_PAGE).then((resultat) => {
        if (!ignore) setPages(resultat.map((page) => page.thumbnailUrl));
      });

    setLoading(true);
    setErreur(false);

    // Un seul await sur `request.get` : son wrapper n'est pas une vraie
    // Promise (cf. assistant RCO), on en sort avant de lire le blob.
    (async () => {
      try {
        const binaire = recapitulatif?.pieceJointeBinaire?.binary;

        if (binaire) {
          // En local, le binaire est sérialisé dans le détail
          await afficher(binaire);
        } else {
          // Déployé : récupéré via l'endpoint de streaming
          const blob = (await request.get(
            getHubeeDocumentContenu(
              identifiantTeledossier,
              recapitulatif.identifiant,
            ),
            { headers: { Accept: '*/*' }, responseType: 'blob' },
          )) as Blob;
          await afficher(arrayBufferToBase64(await blob.arrayBuffer()));
        }
      } catch {
        if (!ignore) setErreur(true);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [recapitulatif, identifiantTeledossier]);

  if (!recapitulatif) {
    return null;
  }

  return (
    <>
      <Button
        data-testid="assistant-recapitulatif-bouton"
        onClick={() => setVisible((prev) => !prev)}
        style={{
          position: 'fixed',
          margin: 0,
          right: 0,
          top: 'calc(50% - 20px)',
          zIndex: 1001,
          borderTopLeftRadius: '50px',
          borderBottomLeftRadius: '50px',
          height: '70px',
          width: '80px',
          alignItems: 'center',
          justifyItems: 'center',
          backgroundColor: COLORS.Background,
          color: 'white',
          cursor: 'pointer',
          transform: visible ? 'translateX(-34vw)' : 'translateX(0)',
          transition: 'transform 0.4s linear',
          padding: visible ? undefined : '0',
        }}
      >
        {visible ? (
          <Icon name="angle right" />
        ) : (
          <Image
            className="sidebar-icon"
            style={{ width: '40px', height: '40px' }}
          />
        )}
      </Button>

      <Sidebar
        data-testid="assistant-recapitulatif"
        animation="overlay"
        direction="right"
        visible={visible}
        width="very wide"
        style={{
          backgroundColor: 'white',
          width: '34vw',
          padding: '1em',
          overflowY: 'auto',
          transitionBehavior: 'normal, normal',
          transitionDuration: '0.4s, 0.4s',
          transitionTimingFunction: 'linear, linear',
          transitionDelay: '0s, 0s',
          transitionProperty: 'transform, box-shadow',
        }}
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <p>Chargement du récapitulatif...</p>
          </div>
        )}
        {erreur && (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <p>Le récapitulatif de la demande n&apos;a pas pu être affiché.</p>
          </div>
        )}
        {!loading && !erreur && pages.length > 0 && (
          // Pages séparées, chacune comme une feuille : sans cela elles
          // s'enchaînent sans démarcation visible.
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
            {pages.map((page, index) => (
              <Image
                // biome-ignore lint/suspicious/noArrayIndexKey: pages d'un PDF, ordre fixe
                key={index}
                src={page}
                alt={`Récapitulatif, page ${index + 1}`}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
                }}
              />
            ))}
          </div>
        )}
      </Sidebar>
    </>
  );
};

export default SidebarRecapitulatif;
